import { Container } from 'Components/Container';
import { TitleForm } from 'Components/Form/styles';
import { FormSuccess } from 'Components/Form/Success';
import { Row } from 'Components/Grid';
import { StepperForm } from 'Components/StepperForm';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { addCompany, editCompany, getCompanyById } from 'services/company-service';
import { stripTaxIdFormatting } from 'utils/tax_id_validators';
import { Detalhes } from './Details';
import { Managers } from './Managers';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useQuery } from 'react-query';
import { PageLoaderForm } from 'Components/PageLoader/Form';
import { useTranslation } from 'react-i18next';

export const CompaniesForm = () => {
  const { t } = useTranslation(['company']);
  const steps = [t('company:companies.form.detailsStep'), t('company:companies.form.managersStep')];
  const { id } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const [idCompany, setIdCompany] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [finished, setFinished] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    shouldUnregister: false,
    defaultValues: {
      active: true,
      cnpj: '',
      corporateName: '',
      country: '',
      defaultLanguage: '',
      description: '',
      domains: [],
      income: '',
      industry: '',
      managers: [],
      name: '',
      partner: false,
      roles: [],
      size: '',
      slug: '',
      signedPlan: 'Free',
    },
  });

  const totalSteps = () => {
    return steps.length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const handleNext = () => {
    const newActiveStep = isLastStep() ? activeStep : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const getTitleStep = () => {
    if (finished) return <></>;

    if (activeStep === 0)
      return (
        <Row>
          <TitleForm>{t('company:companies.form.detailsTitle')}</TitleForm>
        </Row>
      );

    return (
      <Row>
        <TitleForm>{t('company:companies.form.managersTitle')}</TitleForm>
      </Row>
    );
  };

  const handleReset = () => {
    reset();
    setActiveStep(0);
    setFinished(false);
    setIdCompany(null);
    navigate('/companies/add');
  };

  const transformData = data => {
    return {
      ...data,
      cnpj: stripTaxIdFormatting(data.cnpj),
      managers: [
        ...data.managers.map(manager => {
          return {
            _id: manager._id,
            name: manager.name,
          };
        }),
      ],
    };
  };

  const handleComplete = async () => {
    try {
      const res = id ? await editCompany(id, transformData(getValues())) : await addCompany(transformData(getValues()));
      if (res.status === 200 || res.status === 201) {
        setIdCompany(res.data._id ?? id);
        setFinished(true);
      } else {
        throw new Error('Ocorreu um erro ao tentar salvar/editar a Empresa!');
      }
    } catch (error) {
      SwalReact.fire({
        title: t('company:companies.form.errorTitle'),
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const {
    isLoading,
    error,
    data: company,
  } = useQuery(['getCompany', id], async () => {
    if (!id) return null;
    const resCompany = await getCompanyById(id);

    if (!resCompany.data) return null;

    return resCompany.data;
  });

  useEffect(() => {
    if (!id) return;
    if (!company) return;

    Object.keys(company).forEach(key => {
      setValue(key, company[key]);
    });

    return () => {};
  }, [id, company, setValue]);

  const showForm = () => {
    if (id) return !isLoading && !error;
    return true;
  };

  return (
    <Container
      title={id ? t('company:companies.form.editTitle') : t('company:companies.form.addTitle')}
      breadcrumb={t('company:companies.form.breadcrumb')}
      linkPage="/companies"
    >
      {isLoading && <PageLoaderForm />}
      {showForm() && (
        <>
          <StepperForm activeStep={activeStep} steps={steps} />
          <div className="card-content">
            {getTitleStep()}
            {activeStep === 0 && (
              <Detalhes
                control={control}
                register={register}
                handleNext={handleNext}
                handleSubmit={handleSubmit}
                errors={errors}
                getValues={getValues}
              />
            )}

            {activeStep === 1 && !finished && (
              <Managers
                control={control}
                register={register}
                handleBack={handleBack}
                handleComplete={handleComplete}
                handleSubmit={handleSubmit}
                errors={errors}
                getValues={getValues}
              />
            )}
            {finished && (
              <FormSuccess
                title={t('company:companies.form.success')}
                labelStartAgain={t('company:companies.form.addAnother')}
                handleStartAgain={() => handleReset()}
                labelShow={t('company:companies.form.viewDetails')}
                handleShow={() => navigate('/companies/show/' + idCompany)}
              />
            )}
          </div>
        </>
      )}
    </Container>
  );
};
